// Who a team on a Stats tile is compared with (Ben, 2026-11-01: on a team "we lose the year
// selection drop down. We also lose any ability to filter the range"). The same two rows as a
// player's (rules.md "Two rows, two jobs"): the season dropdown moves the team's dot; the Season
// and League chips pick the group, one /team-stats answer per league season in it.
//
// - Season chip: untouched, the team's season (quiet); another season; "every season but …"; or
//   removed, every season (the player's SeasonChip, live/stats-files.ts).
// - League chip: untouched, the team's league (quiet); tick others to add their teams; removed,
//   nothing to compare with (never the whole world, as the player's League chip).

import type { TeamStatsRow } from "@catbento/shared";
import { groupSeasons, seasonLabel, type SeasonChip } from "./stats-files.js";
import { isExcludeValue } from "../engines/stats-filter-kit.js";
import type { TeamStatBar } from "./team-stats.js";

export interface TeamGroupPick {
  competitionId: number;
  season: string;
}

/** The league seasons the group is made of: per league, the Season chip's seasons in that
 *  league's own list (a calendar-year league matches the club season that started the same year,
 *  Q-006). A league without that season adds nothing. Pure. */
export function teamGroupPicks(leagues: readonly { id: number; seasons: readonly string[] }[], chip: SeasonChip, teamSeason: string): TeamGroupPick[] {
  return leagues.flatMap((l) =>
    groupSeasons(chip, Number(teamSeason), l.seasons.map(Number)).seasons.map((s) => ({ competitionId: l.id, season: String(s) })),
  );
}

/** One stat's bar: the team's own value against the group's. The band is the group's lowest to
 *  highest; the track also reaches the team when it sits outside the group (another league, say).
 *  Null track or band with fewer than two values. Pure. */
export function teamGroupBar(rows: readonly TeamStatsRow[], own: number | null | undefined, key: string): TeamStatBar & { band: { min: number; max: number } | null } {
  const values = rows
    .map((r) => r.values[key])
    .filter((v): v is number => v !== null && v !== undefined && !Number.isNaN(v))
    .sort((a, b) => a - b);
  const value = own === null || own === undefined || Number.isNaN(own) ? null : own;
  const band = values.length >= 2 ? { min: values[0]!, max: values[values.length - 1]! } : null;
  const track = band && value !== null ? { min: Math.min(band.min, value), max: Math.max(band.max, value) } : band;
  return { value, values, track, band };
}

/** What the group counts: "teams" in one season; "team-seasons" across several, where a team is
 *  counted once per season it played. Pure. */
export function teamGroupNoun(chip: SeasonChip): string {
  return chip === null || isExcludeValue(chip) ? "team-seasons" : "teams";
}

/** The readout's words for the group: "Premier League (England) teams, 26/27", "3 leagues'
 *  team-seasons, every season". Pure. */
export function teamGroupLabel(leagues: readonly string[], chip: SeasonChip, teamSeason: string): string {
  const noun = teamGroupNoun(chip);
  const who = leagues.length === 1 ? `${leagues[0]} ${noun}` : `${leagues.length} leagues' ${noun}`;
  const when =
    chip === null
      ? "every season"
      : isExcludeValue(chip)
        ? `every season but ${seasonLabel(Number(chip.value))}`
        : seasonLabel(Number(chip === undefined ? teamSeason : chip));
  return `${who}, ${when}`;
}
